import { api } from './api';

export type MisPeriod = 'month' | 'quarter' | 'year';
export type Trend = 'up' | 'down' | 'stable';

export interface MisPeriodInfo {
  period: MisPeriod;
  // YYYY-MM-DD, both ends included
  from: string;
  to: string;
  previousFrom: string;
  previousTo: string;
}

export interface ExecutiveSummary {
  period: MisPeriodInfo;
  // growth is null when the stretch before had no sales to compare with
  revenue: { current: number; previous: number; growth: number | null; trend: Trend; orders: number };
  purchases: { current: number; previous: number; growth: number | null; orders: number };
  // Sales before tax against what the goods cost at today's cost prices
  grossMargin: { revenueExTax: number; costOfGoods: number; percent: number | null; previousPercent: number | null };
  receivables: { outstanding: number; outstandingCount: number; overdue: number; overdueCount: number };
  orders: {
    openSales: number;
    overdueSales: number;
    openPurchases: number;
    overduePurchases: number;
    openWorkOrders: number;
    overdueWorkOrders: number;
  };
  delivery: { delivered: number; onTime: number; rate: number | null };
  inventory: { stockValue: number; products: number; lowStock: number; outOfStock: number; turnover: number | null };
}

export interface Kpi {
  key: string;
  name: string;
  // How the value is written: a percentage, a multiple, rupees or a plain count
  unit: 'percent' | 'times' | 'money' | 'count';
  // null when there is nothing to measure yet
  value: number | null;
  previous: number | null;
  trend: Trend;
  higherIsBetter: boolean;
  description: string;
}

export interface PerformanceMetrics {
  period: MisPeriodInfo;
  kpis: Kpi[];
}

export interface TrendAnalysis {
  months: { period: string; sales: number; purchases: number; production: number }[];
  // Sales projected for the next three months; empty when there is too little history
  forecast: { period: string; sales: number }[];
  forecastNote: string | null;
}

export type InsightCategory = 'sales' | 'inventory' | 'finance' | 'operations' | 'customer' | 'supplier' | 'production';
export type InsightPriority = 'low' | 'medium' | 'high' | 'critical';

export interface Insight {
  id: string;
  title: string;
  description: string;
  type: 'trend' | 'anomaly' | 'prediction' | 'recommendation' | 'alert' | 'opportunity';
  category: InsightCategory;
  priority: InsightPriority;
  impactValue?: number | null;
  impactPercentage?: number | null;
  // The page to open for this insight, and the rows behind it
  metrics?: { link?: string | null; linkLabel?: string | null; items?: Record<string, any>[] } | null;
  recommendations?: string[];
  isActive: boolean;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ActionItem {
  text: string;
  insight: string;
  category: InsightCategory;
  link: string | null;
}

export interface StrategicRecommendations {
  immediate: ActionItem[];
  shortTerm: ActionItem[];
  longTerm: ActionItem[];
}

export class MISService {
  static async getExecutiveSummary(period: MisPeriod): Promise<ExecutiveSummary> {
    const response = await api.get<ExecutiveSummary>('/mis/executive-summary', { params: { period } });
    return response.data;
  }

  static async getPerformanceMetrics(period: MisPeriod): Promise<PerformanceMetrics> {
    const response = await api.get<PerformanceMetrics>('/mis/performance-metrics', { params: { period } });
    return response.data;
  }

  static async getTrendAnalysis(periods: number = 12): Promise<TrendAnalysis> {
    const response = await api.get<TrendAnalysis>('/mis/trend-analysis', { params: { periods } });
    return response.data;
  }

  static async getInsights(category?: InsightCategory): Promise<Insight[]> {
    const response = await api.get<Insight[]>('/mis/insights', { params: category ? { category } : {} });
    return response.data;
  }

  static async getStrategicRecommendations(): Promise<StrategicRecommendations> {
    const response = await api.get<StrategicRecommendations>('/mis/recommendations');
    return response.data;
  }

  // Works the insights out again now, instead of waiting for the hourly run
  static async generateInsights(): Promise<{ message: string; active: number; retired: number }> {
    const response = await api.post('/mis/generate-insights');
    return response.data;
  }
}
